import { useState, useEffect } from "react";
import BottomNav from "../components/BottomNav";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { transactionsApi } from "../services/api";

const T = { night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3",
  cyanL:"#E0F7FA", white:"#fff", bg:"#F7F9FC", border:"#E2E8F0",
  red:"#EF4444", green:"#16A34A", sub:"#64748B" };

function formatDate(dateStr, lang) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return dateStr; }
}

export default function AgreementResponse() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const { reference } = useParams();
  const nav = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [responding, setResponding] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    transactionsApi.getByReference(reference)
      .then(r => setTransaction(r.data?.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [reference]);

  const respond = async (accepted) => {
    setResponding(true);
    try {
      const r = await transactionsApi.respond(reference, { accepted });
      setResult(r.data?.data);
      toast.success(accepted ? t("agreement.accepted_title") : t("agreement.rejected_title"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("agreement.error"));
    } finally { setResponding(false); }
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center",
      justifyContent:"center", background:T.bg }}>
      <div style={{ color:T.sub }}>{t("agreement.loading")}</div>
    </div>
  );

  if (notFound || !transaction) return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ minHeight:"70vh", display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center", padding:"24px", textAlign:"center" }}>
        <div style={{ fontSize:"48px", marginBottom:"16px" }}>🔍</div>
        <h2 style={{ fontFamily:"'Playfair Display',serif", color:T.night, marginBottom:"8px" }}>
          {t("agreement.not_found_title")}
        </h2>
        <p style={{ color:T.sub, fontSize:"14px", marginBottom:"20px" }}>
          {t("agreement.not_found_desc")}
        </p>
        <Link to="/dashboard" style={{ color:T.royal, fontSize:"14px", fontWeight:700, textDecoration:"none" }}>
          {t("agreement.back_dashboard")}
        </Link>
      </div>
      <BottomNav />
    </div>
  );

  const status = result?.transaction?.status || transaction.status;
  const certificate = result?.certificate;

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ background:`linear-gradient(135deg,${T.cyan},#0E8FA3)`,
        padding:"20px 24px", display:"flex", alignItems:"center", gap:"16px" }}>
        <button onClick={() => nav("/dashboard")}
          style={{ background:"transparent", border:"none", color:T.white,
            fontSize:"20px", cursor:"pointer" }}>←</button>
        <h1 style={{ color:T.white, fontSize:"17px", fontWeight:700, margin:0 }}>
          {t("agreement.title")}
        </h1>
      </div>

      <div style={{ maxWidth:"600px", margin:"0 auto", padding:"24px 24px 80px" }}>

        {status === "CONFIRME" ? (
          <StatusPanel icon="✅" color={T.green} bg="#F0FDF4"
            title={t("agreement.accepted_title")} desc={t("agreement.accepted_desc")}>
            {certificate && (
              <div style={{ background:T.white, borderRadius:"10px", padding:"14px 16px",
                marginTop:"16px", textAlign:"left" }}>
                <Row label={t("agreement.certificate_reference")} value={certificate.reference} />
                <Row label={t("agreement.certificate_hash")} value={certificate.verificationHash} />
                <p style={{ fontSize:"11px", color:T.sub, marginTop:"10px", marginBottom:0 }}>
                  {t("agreement.verify_hint")}
                </p>
              </div>
            )}
          </StatusPanel>
        ) : status === "REFUSE" ? (
          <StatusPanel icon="✕" color={T.red} bg="#FEF2F2"
            title={t("agreement.rejected_title")} desc={t("agreement.rejected_desc")} />
        ) : (
          <>
            <div style={{ background:T.white, borderRadius:"16px",
              border:`1.5px solid ${T.border}`, padding:"20px", marginBottom:"20px" }}>
              <span style={{ fontSize:"11px", fontWeight:700, color:T.cyan,
                background:T.cyanL, padding:"3px 10px", borderRadius:"20px",
                textTransform:"uppercase", letterSpacing:"1px" }}>
                {t(`agreement.status.${transaction.status}`, { defaultValue: transaction.status })}
              </span>

              <div style={{ marginTop:"16px", display:"flex", flexDirection:"column", gap:"14px" }}>
                <Row label={t("agreement.from")}
                  value={[transaction.partie1?.firstName, transaction.partie1?.lastName]
                    .filter(Boolean).join(" ")} />
                <Row label={t("agreement.type")}
                  value={t(`transaction_types.${transaction.transactionType}`,
                    { defaultValue: transaction.transactionType })} />
                <Row label={t("agreement.amount")}
                  value={transaction.declaredAmount
                    ? `${Number(transaction.declaredAmount).toLocaleString()} ${t("common.currency")}`
                    : t("agreement.no_amount")} />
                {transaction.actLocation && (
                  <Row label={t("agreement.location")} value={transaction.actLocation} />
                )}
                <Row label={t("agreement.date")} value={formatDate(transaction.actDate, lang)} />
              </div>

              {transaction.description && (
                <div style={{ marginTop:"16px", padding:"12px 14px", background:"#F8FAFC",
                  borderRadius:"10px", fontSize:"13px", color:"#1E293B", lineHeight:"1.6" }}>
                  {transaction.description}
                </div>
              )}
            </div>

            <div style={{ background:T.cyanL, borderLeft:`4px solid ${T.cyan}`,
              padding:"12px 16px", borderRadius:"0 12px 12px 0",
              fontSize:"12.5px", color:"#0E8FA3", marginBottom:"20px" }}>
              {t("agreement.confirm_warning")}
            </div>

            <div style={{ display:"flex", gap:"12px" }}>
              <button onClick={() => respond(false)} disabled={responding}
                style={{ flex:1, padding:"14px", background:"transparent",
                  color:T.red, border:"1.5px solid #FECACA", borderRadius:"12px",
                  fontWeight:700, cursor:responding?"not-allowed":"pointer", fontSize:"14px" }}>
                {t("agreement.reject_btn")}
              </button>
              <button onClick={() => respond(true)} disabled={responding}
                style={{ flex:1, padding:"14px", background:responding?"#E2E8F0":T.cyan,
                  color:responding?"#94A3B8":T.white, border:"none", borderRadius:"12px",
                  fontWeight:700, cursor:responding?"not-allowed":"pointer", fontSize:"14px" }}>
                {t("agreement.accept_btn")}
              </button>
            </div>
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function StatusPanel({ icon, color, bg, title, desc, children }) {
  return (
    <div style={{ background:bg, border:`2px solid ${color}30`, borderRadius:"16px",
      padding:"32px 24px", textAlign:"center" }}>
      <div style={{ fontSize:"48px", marginBottom:"12px" }}>{icon}</div>
      <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"20px",
        fontWeight:700, color, marginBottom:"8px" }}>{title}</h2>
      <p style={{ color:T.sub, fontSize:"13px", lineHeight:"1.6" }}>{desc}</p>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", gap:"12px" }}>
      <span style={{ fontSize:"12px", color:"#94A3B8" }}>{label}</span>
      <span style={{ fontSize:"13px", fontWeight:600, color:"#1E293B", textAlign:"right" }}>{value}</span>
    </div>
  );
}
